package com.xtream.data_kmp.db.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import com.xtream.data_kmp.db.StreamTypeConverter
import com.xtream.data_kmp.db.tables.HiddenCategoryEntity
import com.xtream.data_kmp.db.tables.StreamType
import kotlin.Boolean
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class HiddenCategoryDao_Impl(
  __db: RoomDatabase,
) : HiddenCategoryDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfHiddenCategoryEntity: EntityInsertAdapter<HiddenCategoryEntity>

  private val __streamTypeConverter: StreamTypeConverter = StreamTypeConverter()

  private val __deleteAdapterOfHiddenCategoryEntity:
      EntityDeleteOrUpdateAdapter<HiddenCategoryEntity>
  init {
    this.__db = __db
    this.__insertAdapterOfHiddenCategoryEntity = object : EntityInsertAdapter<HiddenCategoryEntity>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `hidden_categories` (`categoryId`,`type`,`hiddenAt`) VALUES (?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: HiddenCategoryEntity) {
        statement.bindLong(1, entity.categoryId.toLong())
        val _tmp: String = __streamTypeConverter.fromStreamType(entity.type)
        statement.bindText(2, _tmp)
        statement.bindLong(3, entity.hiddenAt)
      }
    }
    this.__deleteAdapterOfHiddenCategoryEntity = object : EntityDeleteOrUpdateAdapter<HiddenCategoryEntity>() {
      protected override fun createQuery(): String = "DELETE FROM `hidden_categories` WHERE `categoryId` = ? AND `type` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: HiddenCategoryEntity) {
        statement.bindLong(1, entity.categoryId.toLong())
        val _tmp: String = __streamTypeConverter.fromStreamType(entity.type)
        statement.bindText(2, _tmp)
      }
    }
  }

  public override suspend fun hideCategory(category: HiddenCategoryEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfHiddenCategoryEntity.insert(_connection, category)
  }

  public override suspend fun hideCategories(categories: List<HiddenCategoryEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfHiddenCategoryEntity.insert(_connection, categories)
  }

  public override suspend fun unhideCategory(category: HiddenCategoryEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __deleteAdapterOfHiddenCategoryEntity.handle(_connection, category)
  }

  public override suspend fun isHidden(categoryId: Int, type: StreamType): Boolean {
    val _sql: String = """
        |
        |        SELECT EXISTS(
        |            SELECT 1
        |            FROM hidden_categories
        |            WHERE categoryId = ?
        |            AND type = ?
        |        )
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, categoryId.toLong())
        _argIndex = 2
        val _tmp: String = __streamTypeConverter.fromStreamType(type)
        _stmt.bindText(_argIndex, _tmp)
        val _result: Boolean
        if (_stmt.step()) {
          val _tmp_1: Int
          _tmp_1 = _stmt.getLong(0).toInt()
          _result = _tmp_1 != 0
        } else {
          _result = false
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getHiddenCategories(type: StreamType): List<HiddenCategoryEntity> {
    val _sql: String = """
        |
        |        SELECT *
        |        FROM hidden_categories
        |        WHERE type = ?
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        val _tmp: String = __streamTypeConverter.fromStreamType(type)
        _stmt.bindText(_argIndex, _tmp)
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfType: Int = getColumnIndexOrThrow(_stmt, "type")
        val _columnIndexOfHiddenAt: Int = getColumnIndexOrThrow(_stmt, "hiddenAt")
        val _result: MutableList<HiddenCategoryEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: HiddenCategoryEntity
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpType: StreamType
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfType)
          _tmpType = __streamTypeConverter.toStreamType(_tmp_1)
          val _tmpHiddenAt: Long
          _tmpHiddenAt = _stmt.getLong(_columnIndexOfHiddenAt)
          _item = HiddenCategoryEntity(_tmpCategoryId,_tmpType,_tmpHiddenAt)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getHiddenCategoryIds(type: StreamType): List<Int> {
    val _sql: String = """
        |
        |        SELECT categoryId
        |        FROM hidden_categories
        |        WHERE type = ?
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        val _tmp: String = __streamTypeConverter.fromStreamType(type)
        _stmt.bindText(_argIndex, _tmp)
        val _result: MutableList<Int> = mutableListOf()
        while (_stmt.step()) {
          val _item: Int
          _item = _stmt.getLong(0).toInt()
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun unhideCategory(categoryId: Int, type: StreamType) {
    val _sql: String = """
        |
        |        DELETE FROM hidden_categories
        |        WHERE categoryId = ?
        |        AND type = ?
        |    
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, categoryId.toLong())
        _argIndex = 2
        val _tmp: String = __streamTypeConverter.fromStreamType(type)
        _stmt.bindText(_argIndex, _tmp)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun clearAll() {
    val _sql: String = "DELETE FROM hidden_categories"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun clearByType(type: StreamType) {
    val _sql: String = """
        |
        |        DELETE FROM hidden_categories
        |        WHERE type = ?
        |    
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        val _tmp: String = __streamTypeConverter.fromStreamType(type)
        _stmt.bindText(_argIndex, _tmp)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
